<script setup lang="ts">
import { CalendarClock, CircleArrowLeft, Rocket, Webhook } from '@lucide/vue'
import {
    DEFAULT_SCHEDULE_TIMEZONE,
    describeSchedule,
    FOLLOW_ENTERPRISE_TIMEZONE,
    nextScheduleFire,
    SCHEDULE_PRESETS,
    scheduleError,
    type TaskSchedule,
} from '@zerohuman/shared'
import {
    Button,
    Checkbox,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Textarea,
    ZhPanel,
    ZhSectionHeading,
    ZhStatusPill,
    ZhSystemLabel,
} from '@zerohuman/ui'
import { computed, ref, useId, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { api, refusalCode, type TaskDetail, type TaskInputField, type TaskLink } from '@/api'
import CopyButton from '@/components/CopyButton.vue'
import { availableTimezones } from '@/lib/timezone'

const props = defineProps<{
    task: TaskDetail
    inputFields: TaskInputField[]
    predecessors: TaskLink[]
    /** OS tasks belong to the harness: their triggers are shown, never edited. */
    editable: boolean
}>()
const emit = defineEmits<{ changed: [] }>()

/** The cron box only appears for this choice, so the presets stay one click. */
const CUSTOM = 'custom'

/*
 * Every block, chip and control here is the kit's (#1227): kit surfaces, the kit status pill and system label, and
 * the kit `Input`, `Select`, `Textarea` and `Checkbox`. A Select's trigger is a button, so it is named by the words
 * beside it rather than wrapped in a label.
 */
const fieldId = useId()

const editing = ref(false)
const saving = ref(false)
const error = ref<string | null>(null)
const form = ref(blankForm())

const schedule = computed<TaskSchedule | null>(() => props.task.schedule ?? null)
/**
 * The zone this enterprise runs in (#884). Every cadence sentence, preview and validity check below is read in
 * it, so a schedule that follows the enterprise shows the zone it actually fires in rather than the UTC an
 * absent argument falls back to. Undefined on an API older than #884, which is exactly that fallback.
 */
const enterpriseTimezone = computed(() => props.task.enterpriseTimezone)
const cadence = computed(() => (schedule.value ? describeSchedule(schedule.value, enterpriseTimezone.value) : null))
/**
 * The firing the worker has booked. A cadence that no worker has picked up yet — just migrated, or saved while
 * nothing was running — has none booked, so fall back to the firing it will take: the same answer, worked out
 * with the same function the worker uses.
 */
const nextRunAt = computed(() => {
    if (props.task.scheduleNextRunAt) {
        return new Date(props.task.scheduleNextRunAt)
    }
    return schedule.value ? nextScheduleFire(schedule.value, new Date(), enterpriseTimezone.value) : null
})
const lastRunAt = computed(() => (props.task.scheduleLastRunAt ? new Date(props.task.scheduleLastRunAt) : null))
const scheduleInput = computed(() => Object.entries(schedule.value?.input ?? {}))

/**
 * The zones a schedule can name for itself. The "follow the enterprise" choice is not one of them — it is the
 * sentinel, offered as its own option above this list — so it is filtered out of the schedule's current value.
 */
const timezones = computed(() => {
    const here = Intl.DateTimeFormat().resolvedOptions().timeZone
    const own = schedule.value?.timezone === FOLLOW_ENTERPRISE_TIMEZONE ? undefined : schedule.value?.timezone
    return availableTimezones(DEFAULT_SCHEDULE_TIMEZONE, here, own)
})

/**
 * What "follow the enterprise" reads as in the picker: the zone it resolves to today, so the choice is
 * concrete rather than a word nobody can check. Changing the enterprise's zone moves every schedule on it.
 */
const followEnterpriseLabel = computed(
    () => `Follow the enterprise (${enterpriseTimezone.value?.trim() || DEFAULT_SCHEDULE_TIMEZONE})`,
)

/** The schedule the form is describing, or the reason it is not one yet. */
const draft = computed<{ schedule: TaskSchedule } | { problem: string }>(() => {
    const cron = form.value.preset === CUSTOM ? form.value.cron : form.value.preset
    let input: Record<string, unknown> | null = null
    try {
        const parsed = form.value.input.trim() === '' ? null : (JSON.parse(form.value.input) as unknown)
        if (parsed !== null && (typeof parsed !== 'object' || Array.isArray(parsed))) {
            return { problem: 'Run input must be a JSON object, like {"repo": "owner/name"}.' }
        }
        input = parsed as Record<string, unknown> | null
    } catch {
        return { problem: 'Run input is not valid JSON.' }
    }
    const candidate: TaskSchedule = {
        cron: cron.trim(),
        timezone: form.value.timezone,
        enabled: form.value.enabled,
        input,
    }
    const problem = scheduleError(candidate, enterpriseTimezone.value)
    return problem ? { problem } : { schedule: candidate }
})

const draftProblem = computed(() => ('problem' in draft.value ? draft.value.problem : null))
/** Answers "what does this cadence actually do?" before the CEO commits to it. */
const draftNextRun = computed(() => {
    if (!('schedule' in draft.value)) {
        return null
    }
    return nextScheduleFire(draft.value.schedule, new Date(), enterpriseTimezone.value)
})

/**
 * A cadence is a plan rather than something that fires when no scheduler is alive. The API answers this
 * from a live worker's heartbeat, not from its own `ENABLE_SCHEDULES` — it never schedules anything itself.
 */
const schedulesOff = computed(() => props.task.schedulesEnabled === false)

/**
 * Firings refused in a row as not runnable before the panel says a person must step in (#1136, CTO decision 2).
 * One alone can be a placement edit caught mid-save; two in a row is structural. A busy skip never counts towards
 * it: skipping while the task or its member is busy is what an overlap-skipping task is for. Nor does a start that
 * failed without a refusal code (`start_failed`): that can be a passing fault, not something a person must fix. Nor
 * does a switched-off task (`task_disabled`): a person paused it on purpose.
 */
const REFUSAL_STREAK = 2
const refusalStreak = computed(() => props.task.scheduleNotRunnableStreak ?? 0)
const refused = computed(() => Boolean(schedule.value) && refusalStreak.value >= REFUSAL_STREAK)
const refusedAt = computed(() => (props.task.scheduleRefusedAt ? new Date(props.task.scheduleRefusedAt) : null))
const taskName = computed(() => props.task.name || props.task.slug)

/** What a refusal code means for the person reading the panel, with the code kept beside it. */
const REFUSAL_REASONS: Record<string, string> = {
    task_not_runnable: 'nobody on the team the task names holds its role, or the task can no longer start',
    no_enterprise: 'the task belongs to no enterprise',
    overlap: 'the latest firing found the task already running',
    member_busy: 'the latest firing found the member it runs on busy',
    start_failed: 'the latest start failed without a refusal code, which can be a passing fault',
    task_disabled: 'the task is switched off, so its firings start nothing until it is switched back on',
    last_run_failed:
        'the last run failed, so its firings start nothing until that run is retried or cleared on Blockers',
    no_ai_account: 'no AI account is connected, so its firings start nothing until one is',
}

/**
 * Whether the latest firing waited for an AI account (#1071): a quiet refusal, so it never reaches the streak alert,
 * but the panel still says why the schedule has not run. A firing that starts clears it.
 */
const waitingForAiAccount = computed(() => props.task.scheduleRefusalReason === 'no_ai_account')

const refusalWhy = computed(() => {
    const reason = props.task.scheduleRefusalReason
    if (!reason) {
        return null
    }
    return `${REFUSAL_REASONS[reason] ?? 'the start was refused'} (${reason})`
})

const webhook = computed(() => props.task.webhook ?? null)

/** What the snippet shows for an input with no default: a number, a boolean, or a placeholder naming the key. */
function sampleValue(field: TaskInputField): unknown {
    if (field.type === 'number') {
        return 0
    }
    if (field.type === 'boolean') {
        return false
    }
    if (field.type === 'file') {
        // A file is started with as a `data:` URL; a path or a link is refused (os#679).
        return `data:<type>;name=<file name>;base64,<${field.key}>`
    }
    return `<${field.key}>`
}

/** A body carrying every input the task declares, so the snippet can be pasted and edited rather than researched. */
const webhookBody = computed(() => {
    const body: Record<string, unknown> = {}
    for (const field of props.inputFields) {
        body[field.key] = field.default ?? sampleValue(field)
    }
    return JSON.stringify(body)
})

const curlSnippet = computed(() =>
    webhook.value
        ? [
              `curl -X POST '${webhook.value.url}' \\`,
              `  -H '${webhook.value.header}: '"$WEBHOOK_SECRET" \\`,
              `  -H 'content-type: application/json' \\`,
              `  -d '${webhookBody.value}'`,
          ].join('\n')
        : '',
)

/*
 * "When a release goes live" (#2095): the task starts each time a production release of its enterprise goes live,
 * with this input plus `release` (the version and its changes), which the server adds. Its own on/off switch and
 * input, apart from the schedule's, so pausing or removing one never touches the other.
 */
/**
 * When a saved trigger actually starts the task, said wherever it is shown or edited: nothing is started until the
 * enterprise's deploy reports its releases, and a release that is not newer, or that finds the task or its team
 * switched off, starts nothing. Without it the block promised a run on every release that may never come.
 */
const RELEASE_TRIGGER_FIRES_WHEN =
    "Nothing starts this task until your deploy reports a release to this enterprise's releases webhook, using the enterprise's webhook secret. A release no newer than the last one reported, such as a rollback or a repeat, starts nothing, and neither does one that arrives while this task or its team is switched off."
const releaseTrigger = computed(() => props.task.releaseTrigger ?? null)
const releaseInput = computed(() => Object.entries(releaseTrigger.value?.input ?? {}))
const releaseLastFiring = computed(() => {
    const last = releaseTrigger.value?.lastFiring
    return last ? `${last.version}, ${when(new Date(last.at))}` : 'No release has started it yet'
})
const releaseEditing = ref(false)
const releaseForm = ref({ enabled: true, input: '' })
/** Which release button is at work, so it can say so on a slow connection. Both stay disabled through `saving`. */
const releaseAction = ref<'save' | 'remove' | null>(null)

/**
 * What a refused save or removal of the release trigger says. A required input the run input leaves out is named
 * in words, as the schedule's skipped-firing notice names it; any other refusal keeps the server's words in brackets,
 * as the schedule's own messages do.
 */
function releaseFailure(verb: 'save' | 'remove', caught: unknown): string {
    const what = `Could not ${verb} the release trigger`
    if (!(caught instanceof Error)) {
        return `${what}.`
    }
    const field = (caught as Error & { field?: unknown }).field
    if (refusalCode(caught) === 'input_required' && typeof field === 'string') {
        return `${what}: the run input has no ${field}, which this task requires and gives no default for.`
    }
    return `${what} (${caught.message}).`
}

/** The run input the release form holds, or why it is not one: empty is none, anything else a JSON object. */
const releaseDraft = computed<{ input: Record<string, unknown> | null } | { problem: string }>(() => {
    const text = releaseForm.value.input.trim()
    if (text === '') {
        return { input: null }
    }
    try {
        const parsed = JSON.parse(text) as unknown
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return { problem: 'Run input must be a JSON object, like {"maxIssues": 5}.' }
        }
        return { input: parsed as Record<string, unknown> }
    } catch {
        return { problem: 'Run input is not valid JSON.' }
    }
})
const releaseProblem = computed(() => ('problem' in releaseDraft.value ? releaseDraft.value.problem : null))

function startReleaseEditing(): void {
    const current = releaseTrigger.value
    error.value = null
    releaseForm.value = current
        ? {
              enabled: current.enabled,
              input:
                  current.input && Object.keys(current.input).length > 0 ? JSON.stringify(current.input, null, 2) : '',
          }
        : { enabled: true, input: suggestedInput() }
    releaseEditing.value = true
}

async function saveRelease(): Promise<void> {
    if (!('input' in releaseDraft.value)) {
        return
    }
    saving.value = true
    releaseAction.value = 'save'
    error.value = null
    try {
        await api.setReleaseTrigger(props.task.id, {
            enabled: releaseForm.value.enabled,
            input: releaseDraft.value.input,
        })
        releaseEditing.value = false
        emit('changed')
    } catch (caught) {
        error.value = releaseFailure('save', caught)
    } finally {
        saving.value = false
        releaseAction.value = null
    }
}

async function removeRelease(): Promise<void> {
    saving.value = true
    releaseAction.value = 'remove'
    error.value = null
    try {
        await api.clearReleaseTrigger(props.task.id)
        releaseEditing.value = false
        emit('changed')
    } catch (caught) {
        error.value = releaseFailure('remove', caught)
    } finally {
        saving.value = false
        releaseAction.value = null
    }
}

watch(
    () => props.task.id,
    () => {
        editing.value = false
        releaseEditing.value = false
        error.value = null
    },
)

/**
 * A new schedule follows the enterprise by default (#884) rather than pinning `UTC`: a company in
 * `America/New_York` asking for 09:00 means its own 09:00, and defaulting to a literal zone silently pinned
 * every new cadence to UTC. Naming a zone stays one choice away in the picker.
 */
function blankForm() {
    return {
        preset: SCHEDULE_PRESETS[1]?.cron ?? '*/15 * * * *',
        cron: '',
        timezone: FOLLOW_ENTERPRISE_TIMEZONE,
        enabled: true,
        input: '',
    }
}

function startEditing(): void {
    const current = schedule.value
    error.value = null
    form.value = current
        ? {
              preset: SCHEDULE_PRESETS.some(row => row.cron === current.cron) ? current.cron : CUSTOM,
              cron: current.cron,
              timezone: current.timezone,
              enabled: current.enabled,
              input:
                  current.input && Object.keys(current.input).length > 0 ? JSON.stringify(current.input, null, 2) : '',
          }
        : { ...blankForm(), input: suggestedInput() }
    editing.value = true
}

/** A first schedule starts from the task's own input defaults rather than an empty box. */
function suggestedInput(): string {
    const body: Record<string, unknown> = {}
    for (const field of props.inputFields) {
        if (field.default !== undefined && field.default !== null && field.default !== '') {
            body[field.key] = field.default
        }
    }
    return Object.keys(body).length > 0 ? JSON.stringify(body, null, 2) : ''
}

async function save(): Promise<void> {
    if (!('schedule' in draft.value)) {
        return
    }
    saving.value = true
    error.value = null
    try {
        await api.setTaskSchedule(props.task.id, draft.value.schedule)
        editing.value = false
        emit('changed')
    } catch (caught) {
        error.value = caught instanceof Error ? `Could not save the schedule (${caught.message}).` : 'Could not save.'
    } finally {
        saving.value = false
    }
}

async function remove(): Promise<void> {
    saving.value = true
    error.value = null
    try {
        await api.clearTaskSchedule(props.task.id)
        editing.value = false
        emit('changed')
    } catch (caught) {
        error.value = caught instanceof Error ? `Could not remove the schedule (${caught.message}).` : 'Could not save.'
    } finally {
        saving.value = false
    }
}

function when(value: Date | null): string {
    return value ? value.toLocaleString() : '—'
}
</script>

<template>
	<section class="space-y-3" data-testid="task-triggers">
		<div>
			<ZhSectionHeading>Triggers</ZhSectionHeading>
			<p class="text-muted-foreground text-sm">What starts this task: its own schedule, a release going live, a task that runs before it, or a POST from anything outside.</p>
		</div>
		<p v-if="error" class="text-destructive text-sm">{{ error }}</p>

		<ZhPanel as="article" class="space-y-3" data-testid="task-schedule">
			<div class="flex flex-wrap items-start justify-between gap-2">
				<p class="flex items-center gap-2 font-medium">
					<CalendarClock class="size-4 shrink-0" aria-hidden="true" />
					Schedule
				</p>
				<Button v-if="editable && !editing" type="button" size="sm" variant="outline" @click="startEditing">
					{{ schedule ? 'Edit schedule' : 'Add a schedule' }}
				</Button>
			</div>

			<ZhPanel
				v-if="schedulesOff && schedule?.enabled"
				as="p"
				class="flex items-start gap-2 text-sm"
				data-testid="schedules-off"
			>
				<!-- The kit's action-required pill, glyph only: the sentence beside it says what needs doing. -->
				<ZhStatusPill tone="action" :pulse="false" class="shrink-0" aria-hidden="true" />
				<span>Nothing on this deployment is running schedules right now, so this cadence is written down but nothing fires it. Either no worker is deployed, or the one that is has <code class="font-mono">ENABLE_SCHEDULES</code> off.</span>
			</ZhPanel>

			<ZhPanel v-if="refused" as="p" class="flex items-start gap-2 text-sm" data-testid="schedule-refused">
				<!-- The same action-required glyph: the cadence fires, but nothing starts until a person acts. -->
				<ZhStatusPill tone="action" :pulse="false" class="shrink-0" aria-hidden="true" />
				<span>
					<span class="font-medium">{{ taskName }}</span> was refused as not runnable on {{ refusalStreak }} scheduled firings in a row<template v-if="refusedAt">, most recently the {{ when(refusedAt) }} firing</template>.
					<template v-if="refusalWhy">Why: {{ refusalWhy }}.</template>
					Each firing is spent and nothing starts until a person fixes this.
				</span>
			</ZhPanel>

			<template v-if="!editing">
				<template v-if="schedule">
					<p class="text-sm" data-testid="schedule-cadence">
						<span class="font-medium">{{ cadence }}</span>
						<ZhStatusPill v-if="!schedule.enabled" tone="action" :pulse="false" class="ml-2">paused</ZhStatusPill>
						<span class="text-muted-foreground ml-2 font-mono text-xs">{{ schedule.cron }}</span>
					</p>
					<div class="grid gap-2 text-sm sm:grid-cols-2">
						<p>
							<span class="text-muted-foreground">Next run</span><br />
							<span data-testid="schedule-next-run">{{ schedule.enabled ? when(nextRunAt) : 'Paused' }}</span>
						</p>
						<p>
							<span class="text-muted-foreground">Last fired</span><br />
							<span data-testid="schedule-last-run">{{ when(lastRunAt) }}</span>
						</p>
					</div>
					<ZhPanel
						v-if="schedule.skipped"
						as="p"
						class="flex items-start gap-2 text-sm"
						data-testid="schedule-skipped"
					>
						<!-- The kit's action-required pill, glyph only: the sentence beside it says what needs doing. -->
						<ZhStatusPill tone="action" :pulse="false" class="shrink-0" aria-hidden="true" />
						<span>The firing at {{ when(new Date(schedule.skipped.at)) }} started no run: the input has no <code class="font-mono">{{ schedule.skipped.field }}</code>, which this task requires and gives no default for. Every firing is skipped until the input supplies it.</span>
					</ZhPanel>
					<ZhPanel
						v-if="waitingForAiAccount"
						as="p"
						class="flex items-start gap-2 text-sm"
						data-testid="schedule-waiting-ai-account"
					>
						<ZhStatusPill tone="action" :pulse="false" class="shrink-0" aria-hidden="true" />
						<span>Waiting for an AI account. Firings start nothing until one is connected, then the next one runs as usual.</span>
					</ZhPanel>
					<div v-if="scheduleInput.length" class="text-sm">
						<p class="text-muted-foreground">Run input</p>
						<div class="mt-1 flex flex-wrap gap-2">
							<ZhSystemLabel v-for="[key, value] in scheduleInput" :key="key" as="span">
								{{ key }}: {{ String(value) }}
							</ZhSystemLabel>
						</div>
					</div>
				</template>
				<p v-else class="text-muted-foreground text-sm" data-testid="schedule-none">
					No schedule. This task runs only when someone starts it, when a release goes live and it has a release trigger, when a task before it finishes, or when its webhook is called.
				</p>
			</template>

			<form v-else class="space-y-3" @submit.prevent="save">
				<div class="grid gap-3 sm:grid-cols-2">
					<div class="grid gap-1 text-sm">
						<span :id="`${fieldId}-preset`">How often</span>
						<Select v-model="form.preset" data-testid="schedule-preset">
							<SelectTrigger class="w-full" :aria-labelledby="`${fieldId}-preset`">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem v-for="preset in SCHEDULE_PRESETS" :key="preset.cron" :value="preset.cron">{{ preset.label }}</SelectItem>
								<SelectItem :value="CUSTOM">Custom cron…</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div class="grid gap-1 text-sm">
						<span :id="`${fieldId}-timezone`">Timezone</span>
						<Select v-model="form.timezone" data-testid="schedule-timezone">
							<SelectTrigger class="w-full" :aria-labelledby="`${fieldId}-timezone`">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem :value="FOLLOW_ENTERPRISE_TIMEZONE">{{ followEnterpriseLabel }}</SelectItem>
								<SelectItem v-for="zone in timezones" :key="zone" :value="zone">{{ zone }}</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<label v-if="form.preset === CUSTOM" class="grid gap-1 text-sm sm:col-span-2">
						Cron (minute hour day-of-month month day-of-week)
						<Input v-model="form.cron" data-testid="schedule-cron" placeholder="0 9 * * 1-5" />
					</label>
				</div>
				<label class="grid gap-1 text-sm">
					Run input JSON
					<Textarea v-model="form.input" data-testid="schedule-input" rows="4" placeholder='{"repo": "owner/name"}' />
				</label>
				<label class="flex items-center gap-2 text-sm">
					<Checkbox v-model="form.enabled" data-testid="schedule-enabled" />
					Run on this schedule
				</label>
				<p v-if="draftProblem" class="text-destructive text-sm" data-testid="schedule-problem">{{ draftProblem }}</p>
				<p v-else class="text-muted-foreground text-sm" data-testid="schedule-preview">
					{{ form.enabled ? `Next run ${draftNextRun ? draftNextRun.toLocaleString() : 'not within a year'}.` : 'Saved but paused: nothing will fire until you turn it back on.' }}
				</p>
				<div class="flex flex-wrap gap-2">
					<Button type="submit" size="sm" :disabled="saving || Boolean(draftProblem)">Save schedule</Button>
					<Button type="button" size="sm" variant="outline" :disabled="saving" @click="editing = false">Cancel</Button>
					<Button v-if="schedule" type="button" size="sm" variant="destructive" :disabled="saving" @click="remove">Remove schedule</Button>
				</div>
			</form>
		</ZhPanel>

		<ZhPanel as="article" class="space-y-3" data-testid="release-trigger">
			<div class="flex flex-wrap items-start justify-between gap-2">
				<p class="flex items-center gap-2 font-medium">
					<Rocket class="size-4 shrink-0" aria-hidden="true" />
					When a release goes live
				</p>
				<Button
					v-if="editable && !releaseEditing"
					type="button"
					size="sm"
					variant="outline"
					:data-testid="releaseTrigger ? 'release-trigger-edit' : 'release-trigger-add'"
					@click="startReleaseEditing"
				>
					{{ releaseTrigger ? 'Edit release trigger' : 'Add release trigger' }}
				</Button>
			</div>

			<template v-if="!releaseEditing">
				<template v-if="releaseTrigger">
					<p class="text-sm" data-testid="release-trigger-state">
						<span class="font-medium">{{
							releaseTrigger.enabled
								? 'Starts when this enterprise reports a newer production release going live'
								: 'Paused: a release going live does not start this task until the trigger is turned back on'
						}}</span>
						<ZhStatusPill v-if="!releaseTrigger.enabled" tone="action" :pulse="false" class="ml-2">paused</ZhStatusPill>
					</p>
					<p class="text-muted-foreground text-sm" data-testid="release-trigger-how">{{ RELEASE_TRIGGER_FIRES_WHEN }}</p>
					<p class="text-sm">
						<span class="text-muted-foreground">Last release that started it</span><br />
						<span data-testid="release-trigger-last">{{ releaseLastFiring }}</span>
					</p>
					<div v-if="releaseInput.length" class="text-sm">
						<p class="text-muted-foreground">Run input</p>
						<div class="mt-1 flex flex-wrap gap-2">
							<ZhSystemLabel v-for="[key, value] in releaseInput" :key="key" as="span">
								{{ key }}: {{ String(value) }}
							</ZhSystemLabel>
						</div>
					</div>
				</template>
				<p v-else class="text-muted-foreground text-sm" data-testid="release-trigger-none">
					No release trigger. A release this enterprise reports going live does not start this task.
				</p>
			</template>

			<div v-else class="space-y-3">
				<label class="grid gap-1 text-sm">
					Run input JSON
					<Textarea v-model="releaseForm.input" data-testid="release-trigger-input" rows="4" placeholder='{"maxIssues": 5}' />
				</label>
				<label class="flex items-center gap-2 text-sm">
					<Checkbox v-model="releaseForm.enabled" data-testid="release-trigger-enabled" />
					Run when a release goes live
				</label>
				<p v-if="releaseProblem" class="text-destructive text-sm" data-testid="release-trigger-problem">{{ releaseProblem }}</p>
				<p v-else class="text-muted-foreground text-sm">
					Each run also gets <code class="font-mono">release</code>: the version that went live and the changes it carried.
				</p>
				<p class="text-muted-foreground text-sm" data-testid="release-trigger-how">{{ RELEASE_TRIGGER_FIRES_WHEN }}</p>
				<p v-if="releaseTrigger" class="text-muted-foreground text-sm" data-testid="release-trigger-remove-note">
					Removing the release trigger also deletes its run input.
				</p>
				<div class="flex flex-wrap gap-2">
					<Button
						type="button"
						size="sm"
						data-testid="release-trigger-save"
						:disabled="saving || Boolean(releaseProblem)"
						@click="saveRelease"
					>
						{{ releaseAction === 'save' ? 'Saving…' : 'Save release trigger' }}
					</Button>
					<Button
						type="button"
						size="sm"
						variant="outline"
						data-testid="release-trigger-cancel"
						:disabled="saving"
						@click="releaseEditing = false"
					>
						Cancel
					</Button>
					<Button
						v-if="releaseTrigger"
						type="button"
						size="sm"
						variant="destructive"
						data-testid="release-trigger-remove"
						:disabled="saving"
						@click="removeRelease"
					>
						{{ releaseAction === 'remove' ? 'Removing…' : 'Remove release trigger' }}
					</Button>
				</div>
			</div>
		</ZhPanel>

		<ZhPanel v-if="webhook" as="article" class="space-y-3" data-testid="task-webhook">
			<p class="flex items-center gap-2 font-medium">
				<Webhook class="size-4 shrink-0" aria-hidden="true" />
				Webhook
			</p>
			<p class="text-muted-foreground text-sm">A third party starts this task by posting here. The JSON body becomes the run input.</p>
			<div class="flex flex-wrap items-center gap-2">
				<ZhPanel as="code" class="min-w-0 flex-1 overflow-x-auto font-mono text-xs" data-testid="webhook-url">POST {{ webhook.url }}</ZhPanel>
				<CopyButton :value="webhook.url" label="webhook URL" />
			</div>
			<p class="text-muted-foreground text-sm" data-testid="webhook-auth">
				Authenticate with the <code class="font-mono">{{ webhook.header }}</code> header, carrying your enterprise's webhook
				secret. It is shown only when you rotate it, in
				<RouterLink to="/webhooks" class="underline underline-offset-4">Settings → Webhooks</RouterLink>.
			</p>
			<div class="space-y-2">
				<div class="flex items-center justify-between gap-2">
					<p class="text-muted-foreground text-sm">Example</p>
					<CopyButton :value="curlSnippet" label="the curl command" />
				</div>
				<ZhPanel as="pre" class="overflow-x-auto text-xs" data-testid="webhook-curl">{{ curlSnippet }}</ZhPanel>
			</div>
		</ZhPanel>

		<ZhPanel v-if="predecessors.length" as="article" class="space-y-2 text-sm" data-testid="task-trigger-predecessors">
			<p class="flex items-center gap-2 font-medium">
				<CircleArrowLeft class="size-4 shrink-0" aria-hidden="true" />
				After another task
			</p>
			<p class="text-muted-foreground">
				This task also starts when {{ predecessors.map(row => row.name || row.slug).join(', ') }} finishes. That edge lives on the predecessor's successors, not here.
			</p>
		</ZhPanel>
	</section>
</template>
